import { ChevronLeft, ChevronRight, Loader2, Minus, Plus } from "lucide-react"
import * as React from "react"

import { useCtrlWheelZoom, useDragPan } from "@/lib/pan-zoom"
import { cn } from "@/lib/utils"

import type { FindingId } from "../../data/review-data"
import { FindingNumber, MessageButton } from "../finding-parts"
import type { ReportFindingItem } from "../report-data"
import { usePdfPage, useElementSize } from "../use-pdf-page"

import {
  CARD_WIDTH,
  ESTIMATED_CARD_HEIGHT,
  FIT,
  GUTTER,
  MIN_PAGE_WIDTH,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  clampView,
  frameBox,
  layoutCards,
  marksInDrawOrder,
  pinsOnPage,
  project,
  unpinnedFindings,
  zoomAround,
  type Fraction,
  type Pin,
  type View,
} from "./document-layout"

/** Rendered at this multiple of the on-screen size, so the page still reads at the top zoom. */
const RASTER_SCALE = 3

/*
   Fixed colours, never theme tokens, for everything drawn on the page: it is
   always white, and a token that flips in dark mode would vanish on it. The
   brand's and the "master" green's light-theme values.
*/
const ACCENT = "#c8461a"
const DONE = "#1d6b49"

export interface DocumentViewProps {
  /** The printed label. Empty when the review has none attached. */
  documentUrl: string
  /** The outstanding findings, by number, with every located occurrence. */
  findings: readonly ReportFindingItem[]
  unreadFor: (findingId: FindingId) => number
  /** Whether the reader may post, which decides the message button's wording and badge. */
  canPost: boolean
  /** Open a finding's conversation. Omitted, the cards offer no message button. */
  onOpenMessages?: ((findingId: FindingId) => void) | undefined
}

interface Focus {
  id: FindingId
  /** Which of the finding's boxes on this page is framed. */
  index: number
}

/**
 * The label as an annotated proof: the page in the middle, a callout card for
 * each divergence down either side, each joined by a line to where it occurs.
 *
 * The page zooms and pans inside its own frame while the cards hold still.
 * Selecting a card frames its finding; selecting it again steps to the
 * finding's next occurrence on the page. A line whose box has left the frame
 * is hidden and its card dimmed, so no line points at nothing.
 */
export function DocumentView({
  documentUrl,
  findings,
  unreadFor,
  canPost,
  onOpenMessages,
}: DocumentViewProps): JSX.Element {
  const wrapRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const { width: available } = useElementSize(wrapRef)

  // Below this the columns would squeeze the page to a sliver, so they go and
  // the page takes the width.
  const wide = available >= 2 * (CARD_WIDTH + GUTTER) + MIN_PAGE_WIDTH
  const pageLeft = wide ? CARD_WIDTH + GUTTER : 0
  const pageWidth = wide ? available - 2 * (CARD_WIDTH + GUTTER) : available

  const [page, setPage] = React.useState(1)
  const { geometry, loading, failed } = usePdfPage(
    documentUrl,
    page,
    pageWidth,
    canvasRef,
    RASTER_SCALE,
  )
  const W = geometry?.width ?? 0
  const H = geometry?.height ?? 0

  const [view, setView] = React.useState<View>(FIT)
  const [focus, setFocus] = React.useState<Focus | null>(null)

  // Drag to pan a zoomed page; a release glides on until friction or an edge stops it.
  // `from` is the view at the press, which every move is measured from; `live` is the
  // view as last set, which each glide frame steps on from.
  const fromRef = React.useRef<View>(FIT)
  const liveRef = React.useRef<View>(FIT)
  const pan = useDragPan({
    // At fit there is nowhere to pan to; a press is just a press.
    onPress: () => {
      if (view.zoom <= ZOOM_MIN) return false
      fromRef.current = view
      liveRef.current = view
      return true
    },
    onMove: (dx, dy) => {
      const from = fromRef.current
      liveRef.current = clampView({ ...from, tx: from.tx + dx, ty: from.ty + dy }, W, H)
      setView(liveRef.current)
    },
    onGlide: (vx, vy) => {
      const current = liveRef.current
      const next = clampView({ ...current, tx: current.tx + vx, ty: current.ty + vy }, W, H)
      liveRef.current = next
      setView(next)
      return { x: next.tx !== current.tx, y: next.ty !== current.ty }
    },
  })
  const { dragging, gliding, stopGlide } = pan

  // A new page, or a new size, starts again from fit.
  React.useEffect(() => {
    setView(FIT)
    setFocus(null)
  }, [geometry?.page, W])

  const pins = React.useMemo<Pin[]>(
    () =>
      geometry
        ? pinsOnPage(findings, geometry.page, geometry.pointWidth, geometry.pointHeight)
        : [],
    [findings, geometry],
  )

  const [heights, setHeights] = React.useState<ReadonlyMap<FindingId, number>>(new Map())
  const measure = React.useCallback((id: FindingId, height: number) => {
    setHeights((current) => {
      // Only re-lay-out on a real change, or measuring feeds itself.
      if (current.get(id) === height) return current
      const next = new Map(current)
      next.set(id, height)
      return next
    })
  }, [])

  const cards = React.useMemo(
    () => (wide && H > 0 ? layoutCards(pins, heights, H) : []),
    [wide, pins, heights, H],
  )
  const pinById = React.useMemo(() => new Map(pins.map((p) => [p.finding.id, p])), [pins])
  const diagramHeight = Math.max(
    H,
    ...cards.map((c) => c.top + (heights.get(c.id) ?? ESTIMATED_CARD_HEIGHT)),
  )
  const unpinned = unpinnedFindings(findings)
  const focusedPin = focus ? pinById.get(focus.id) : undefined

  const frame = (id: FindingId, index: number): void => {
    const box = pinById.get(id)?.boxes[index]
    if (!box || W <= 0) return
    stopGlide()
    setView(frameBox(box, W, H))
    setFocus({ id, index })
  }

  /** A card: the first press frames its finding; each press after steps to its next occurrence. */
  const selectCard = (id: FindingId): void => {
    const count = pinById.get(id)?.boxes.length ?? 0
    if (count === 0) return
    frame(id, focus?.id === id ? (focus.index + 1) % count : 0)
  }

  // Ctrl + wheel zooms on the pointer, as in a PDF viewer.
  const frameRef = React.useRef<HTMLDivElement>(null)
  const wheeling = useCtrlWheelZoom(frameRef, (factor, x, y) => {
    if (W <= 0) return
    stopGlide()
    setView((current) => zoomAround(current, current.zoom * factor, x, y, W, H))
  })

  const zoomBy = (delta: number): void => {
    stopGlide()
    setView((current) => zoomAround(current, current.zoom + delta, W / 2, H / 2, W, H))
  }
  const fit = (): void => {
    stopGlide()
    setView(FIT)
    setFocus(null)
  }

  const located = pins.length
  const pageCount = geometry?.pageCount ?? 1

  return (
    <div ref={wrapRef} className="w-full">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-body-sm text-text-3">
          {located} of {findings.length} divergence{findings.length === 1 ? "" : "s"} marked on this
          page.{" "}
          {wide ? "Select a card to zoom to where it occurs." : "Select a mark to zoom to it."}
        </p>
        {pageCount > 1 && (
          <div className="flex items-center gap-1 text-body-sm text-text-2">
            <PageButton
              label="Previous page"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1))
              }}
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </PageButton>
            <span className="min-w-20 text-center tabular-nums" aria-live="polite">
              Page {geometry?.page ?? page} / {pageCount}
            </span>
            <PageButton
              label="Next page"
              disabled={page >= pageCount}
              onClick={() => {
                setPage((p) => Math.min(pageCount, p + 1))
              }}
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </PageButton>
          </div>
        )}
      </div>

      {!documentUrl && (
        <Notice>
          The printed label is not attached to this review, so it cannot be shown here. Every
          divergence is listed in full under List view.
        </Notice>
      )}
      {documentUrl && loading && !geometry && (
        <div className="grid h-[420px] place-items-center">
          <Loader2 className="size-5 animate-spin text-text-3 motion-reduce:animate-none" />
          <span className="sr-only">Loading the printed label</span>
        </div>
      )}
      {/* A written sentence, not the renderer's: this is a client deliverable. */}
      {failed && (
        <Notice>
          The label could not be displayed. Every divergence is still listed in full under List
          view.
        </Notice>
      )}

      {/* `isolate`: the cards, lines and zoom controls stack among themselves,
          and never above a pop-up opened over the page. */}
      <div
        className={cn("relative isolate", (!geometry || failed) && "hidden")}
        style={{ height: diagramHeight || undefined }}
      >
        {/* Leader lines: over the page and under the cards, so no card is hidden by its own line. */}
        {wide && geometry && (
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1] h-full w-full overflow-visible"
          >
            {cards.map((card) => {
              const pin = pinById.get(card.id)
              if (!pin) return null
              const at = project(view, pin.x, pin.y, W, H)
              if (!at.visible) return null
              const pinX = pageLeft + at.px
              const cardY = card.top + (heights.get(card.id) ?? ESTIMATED_CARD_HEIGHT) / 2
              const cardEdge = card.side === "left" ? CARD_WIDTH : pageLeft + W + GUTTER
              // The elbow turns in the gutter, so the vertical run never crosses the page.
              const elbowX =
                card.side === "left" ? CARD_WIDTH + GUTTER / 2 : pageLeft + W + GUTTER / 2
              const colour = pin.finding.clientResolution === "done" ? DONE : ACCENT
              const active = focus?.id === card.id
              return (
                <g key={card.id} className="transition-opacity duration-260">
                  <polyline
                    points={`${String(cardEdge)},${String(cardY)} ${String(elbowX)},${String(cardY)} ${String(elbowX)},${String(at.py)} ${String(pinX)},${String(at.py)}`}
                    fill="none"
                    stroke={colour}
                    strokeWidth={active ? 1.5 : 1}
                    strokeDasharray="3 3"
                    // Quiet until selected: on a busy page twenty lines cross the text.
                    opacity={active ? 0.9 : focus ? 0.15 : 0.4}
                  />
                  <circle cx={pinX} cy={at.py} r={active ? 3.5 : 2.5} fill={colour} />
                </g>
              )
            })}
          </svg>
        )}

        {/* The page, zoomed and panned inside its frame. */}
        <div
          className={cn(
            "absolute top-0 touch-none overflow-hidden rounded-lg bg-white shadow-e1 ring-1 ring-border",
            view.zoom > ZOOM_MIN && (dragging ? "cursor-grabbing" : "cursor-grab"),
          )}
          ref={frameRef}
          style={{ left: pageLeft, width: W || undefined, height: H || undefined }}
          {...pan.handlers}
        >
          <div
            className={cn(
              "absolute left-0 top-0 origin-top-left",
              // A drag, a glide or a turning wheel follows the pointer; only a reframe animates.
              !dragging &&
                !gliding &&
                !wheeling &&
                "transition-transform duration-400 ease-out motion-reduce:transition-none",
            )}
            style={{
              width: W || undefined,
              height: H || undefined,
              transform: `translate(${String(view.tx)}px, ${String(view.ty)}px) scale(${String(view.zoom)})`,
            }}
          >
            <canvas
              ref={canvasRef}
              role="img"
              aria-label={`Printed label, page ${String(geometry?.page ?? page)}`}
              className="block select-none"
              style={{ width: W || undefined, height: H || undefined }}
            />
            {marksInDrawOrder(pins, focus?.id ?? null).map(({ pin, box, index }) => (
              <PageMark
                key={`${pin.finding.id}:${String(index)}`}
                box={box}
                finding={pin.finding}
                index={index}
                count={pin.boxes.length}
                zoom={view.zoom}
                focused={focus?.id === pin.finding.id && focus.index === index}
                dimmed={focus !== null && focus.id !== pin.finding.id}
                onSelect={() => {
                  frame(pin.finding.id, index)
                }}
              />
            ))}
          </div>

          <ZoomControls
            zoom={view.zoom}
            onZoomOut={() => {
              zoomBy(-ZOOM_STEP)
            }}
            onZoomIn={() => {
              zoomBy(ZOOM_STEP)
            }}
            onFit={fit}
          />
        </div>

        {cards.map((card) => {
          const pin = pinById.get(card.id)
          if (!pin) return null
          const inView = project(view, pin.x, pin.y, W, H).visible
          return (
            <CalloutCard
              key={card.id}
              finding={pin.finding}
              occurrencesHere={pin.boxes.length}
              active={focus?.id === card.id}
              dimmed={focus !== null && focus.id !== card.id}
              occurrenceIndex={focus?.id === card.id ? focus.index : null}
              outOfView={!inView}
              unread={unreadFor(card.id)}
              canPost={canPost}
              onSelect={() => {
                selectCard(card.id)
              }}
              onOpenMessages={
                onOpenMessages
                  ? () => {
                      onOpenMessages(card.id)
                    }
                  : undefined
              }
              onMeasured={measure}
              style={{
                top: card.top,
                width: CARD_WIDTH,
                left: card.side === "left" ? 0 : pageLeft + W + GUTTER,
              }}
            />
          )
        })}
      </div>

      {/* Without the side cards, the selected finding is named under the page,
          with its conversation one press away. */}
      {!wide && focusedPin && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-surface p-3 shadow-e1">
          <FindingNumber
            number={focusedPin.finding.number}
            settled={focusedPin.finding.clientResolution === "done"}
          />
          <div className="min-w-0 flex-1">
            <p className="text-body-sm font-semibold text-text">{focusedPin.finding.title}</p>
            {focusedPin.boxes.length > 1 && focus && (
              <p className="mt-0.5 text-[11px] font-medium text-text-3">
                Occurrence {focus.index + 1} of {focusedPin.boxes.length}
              </p>
            )}
          </div>
          {onOpenMessages && (
            <MessageButton
              onClick={() => {
                onOpenMessages(focusedPin.finding.id)
              }}
              canPost={canPost}
              unread={unreadFor(focusedPin.finding.id)}
            />
          )}
        </div>
      )}

      {unpinned.length > 0 && (
        <section
          aria-label="Not pinned to a location"
          className="mx-auto mt-6 max-w-[820px] rounded-lg border border-border bg-surface-sunken p-4"
        >
          <h2 className="text-eyebrow uppercase text-text-3">Not pinned to a location</h2>
          <ul className="mt-2 flex flex-col gap-1.5">
            {unpinned.map((f) => (
              <li key={f.id} className="flex items-center gap-2">
                <FindingNumber number={f.number} settled={f.clientResolution === "done"} />
                <span className="min-w-0 flex-1 text-body-sm text-text">{f.title}</span>
                {onOpenMessages && (
                  <MessageButton
                    onClick={() => {
                      onOpenMessages(f.id)
                    }}
                    canPost={canPost}
                    unread={unreadFor(f.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/** One occurrence's box on the page. Counter-scaled so its ring and badge keep one size at any zoom. */
function PageMark({
  box,
  finding,
  index,
  count,
  zoom,
  focused,
  dimmed,
  onSelect,
}: {
  box: Fraction
  finding: ReportFindingItem
  index: number
  count: number
  zoom: number
  focused: boolean
  dimmed: boolean
  onSelect: () => void
}): JSX.Element {
  const colour = finding.clientResolution === "done" ? DONE : ACCENT
  const ring = (focused ? 2.5 : 1.5) / zoom
  const where = count > 1 ? `, occurrence ${String(index + 1)} of ${String(count)}` : ""
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Finding ${String(finding.number)}${where}: ${finding.title}. Zoom to it.`}
      title={`Finding ${String(finding.number)}: ${finding.title}`}
      className="absolute cursor-pointer rounded-[2px] transition-[box-shadow,opacity] duration-260 focus-visible:outline-none"
      style={{
        left: `${String(box.x * 100)}%`,
        top: `${String(box.y * 100)}%`,
        // A floor, so a one-word finding is still a target.
        width: `max(${String(box.w * 100)}%, ${String(8 / zoom)}px)`,
        height: `max(${String(box.h * 100)}%, ${String(8 / zoom)}px)`,
        backgroundColor: focused ? `${colour}38` : `${colour}1f`,
        boxShadow: focused
          ? `0 0 0 ${String(ring)}px ${colour}, 0 0 ${String(12 / zoom)}px ${String(3 / zoom)}px ${colour}73`
          : `0 0 0 ${String(ring)}px ${colour}`,
        opacity: dimmed ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden="true"
        className="absolute -left-2 -top-2 grid origin-top-left place-items-center rounded-full text-[9px] font-bold text-white shadow"
        style={{
          height: 16,
          width: 16,
          backgroundColor: colour,
          transform: `scale(${String(1 / zoom)})`,
        }}
      >
        {finding.number}
      </span>
    </button>
  )
}

function CalloutCard({
  finding,
  occurrencesHere,
  active,
  dimmed,
  occurrenceIndex,
  outOfView,
  unread,
  canPost,
  onSelect,
  onOpenMessages,
  onMeasured,
  style,
}: {
  finding: ReportFindingItem
  occurrencesHere: number
  active: boolean
  /** Another card is selected. */
  dimmed: boolean
  /** Which occurrence is framed, when this card's finding is. */
  occurrenceIndex: number | null
  outOfView: boolean
  unread: number
  canPost: boolean
  onSelect: () => void
  onOpenMessages?: (() => void) | undefined
  onMeasured: (id: FindingId, height: number) => void
  style: React.CSSProperties
}): JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null)
  const id = finding.id

  // Measured rather than assumed: a card's height follows how long its quoted wording is.
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const report = (): void => {
      onMeasured(id, el.offsetHeight)
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [id, onMeasured])

  const step =
    occurrencesHere > 1
      ? occurrenceIndex !== null
        ? `Occurrence ${String(occurrenceIndex + 1)} of ${String(occurrencesHere)} · select again for the next`
        : `${String(occurrencesHere)} occurrences on this page`
      : null

  return (
    <div
      ref={ref}
      style={style}
      className={cn(
        "absolute z-[2] rounded-lg border bg-surface p-3 shadow-e1 transition-[opacity,box-shadow,border-color] duration-260",
        active ? "border-brand ring-2 ring-brand/30" : "border-border hover:border-border-strong",
        // Another card is selected: this one steps back so the selected one
        // leads, and comes forward again under the pointer.
        !active && dimmed && "opacity-45 hover:opacity-100",
        !active && !dimmed && outOfView && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2">
        {/* The zoom button's hit area is stretched over the whole card, so a
            press anywhere on it frames the finding; the message button sits
            above that layer and keeps its own press. */}
        <button
          type="button"
          onClick={onSelect}
          aria-label={`Zoom to finding ${String(finding.number)}: ${finding.title}`}
          aria-pressed={active}
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 rounded-sm text-left after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-[color:var(--brand)]"
        >
          <FindingNumber number={finding.number} settled={finding.clientResolution === "done"} />
          <span
            className={cn(
              // `break-words`: generated titles carry ids like hazards_humans_domestic.
              "min-w-0 flex-1 break-words pt-0.5 text-caption font-semibold leading-snug text-text",
              !active && "line-clamp-3",
            )}
          >
            {finding.title}
          </span>
        </button>
        {onOpenMessages && (
          <span className="relative z-[1]">
            <MessageButton onClick={onOpenMessages} canPost={canPost} unread={unread} />
          </span>
        )}
      </div>

      {step && <p className="mt-2 text-[11px] font-medium text-text-3">{step}</p>}

      {/* Clamped until selected: a column of full quotations would push every
          card far below its pin. The selected card shows the wording in full. */}
      {(finding.printedText ?? finding.masterText) ? (
        <dl className="mt-2.5 divide-y divide-border-soft overflow-hidden rounded-md border border-border-soft">
          <Quote side="printed" text={finding.printedText} full={active} />
          <Quote side="master" text={finding.masterText} full={active} />
        </dl>
      ) : (
        finding.divergence && (
          <p
            className={cn(
              "mt-2 border-t border-border-soft pt-2 text-[11px] leading-snug text-text-2",
              !active && "line-clamp-3",
            )}
          >
            {finding.divergence}
          </p>
        )
      )}
    </div>
  )
}

/**
 * One side of the comparison, as a compact diff row: what is printed first,
 * in red, then what was approved, in green — the same order and colours as
 * the checklist's and the list's printed-versus-master pair, in a form that
 * fits a narrow card.
 */
const QUOTE_SIDE = {
  printed: {
    label: "Printed",
    absent: "Not on the printed label",
    row: "bg-err-tint/50",
    rail: "bg-err",
    tone: "text-err",
  },
  master: {
    label: "Master",
    absent: "Not in the approved master",
    row: "bg-master-tint/50",
    rail: "bg-master",
    tone: "text-master",
  },
} as const

function Quote({
  side,
  text,
  full,
}: {
  side: keyof typeof QUOTE_SIDE
  text: string | null
  full: boolean
}): JSX.Element {
  const s = QUOTE_SIDE[side]
  return (
    <div className={cn("relative py-1.5 pl-3 pr-2.5", s.row)}>
      <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-[3px]", s.rail)} />
      <dt className={cn("text-[9.5px] font-semibold uppercase leading-4 tracking-[0.1em]", s.tone)}>
        {s.label}
      </dt>
      <dd
        className={cn(
          "break-words text-[11.5px] leading-snug text-text",
          !full && "line-clamp-2",
          !text && "italic text-text-3",
        )}
      >
        {text ?? s.absent}
      </dd>
    </div>
  )
}

function ZoomControls({
  zoom,
  onZoomOut,
  onZoomIn,
  onFit,
}: {
  zoom: number
  onZoomOut: () => void
  onZoomIn: () => void
  onFit: () => void
}): JSX.Element {
  return (
    <div
      // Its own presses are never the start of a pan.
      onPointerDown={(event) => {
        event.stopPropagation()
      }}
      title="Ctrl + scroll to zoom, drag to move around"
      className="absolute bottom-2 right-2 z-[3] flex items-center gap-0.5 rounded-full border border-border bg-surface/90 px-1 py-0.5 text-caption font-medium text-text-2 shadow-e1 backdrop-blur"
    >
      <button
        type="button"
        onClick={onZoomOut}
        disabled={zoom <= ZOOM_MIN + 0.001}
        aria-label="Zoom out"
        className="grid size-6 place-items-center rounded-full hover:bg-surface-sunken disabled:opacity-40"
      >
        <Minus className="size-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onFit}
        aria-label="Fit the whole page"
        className="min-w-12 rounded px-1 text-center tabular-nums hover:text-text"
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        onClick={onZoomIn}
        disabled={zoom >= ZOOM_MAX - 0.001}
        aria-label="Zoom in"
        className="grid size-6 place-items-center rounded-full hover:bg-surface-sunken disabled:opacity-40"
      >
        <Plus className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-7 place-items-center rounded-full hover:bg-surface-sunken disabled:opacity-40"
    >
      {children}
    </button>
  )
}

function Notice({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="rounded-lg border border-dashed border-border p-10 text-center text-body-sm text-text-3">
      {children}
    </p>
  )
}
